import React from "react";
import { Form, Input, Row, Col, Radio, DatePicker, TimePicker } from "antd";
import moment from "moment";
import { VISITOR_INFO_TYPES } from "@iso/constants/visitor";

/**
 * The Visitor Detail field set — Type, arrival, identity, contact and emergency
 * contact.
 *
 * Extracted from VisitorInfoModal so the induction flow can present the SAME
 * screen as its first step without duplicating the layout or the validation
 * rules. Both callers render this inside their own <Form>; only the shell
 * (modal chrome vs wizard step) differs.
 *
 * The two read-only flags exist for the induction context:
 *  - `typeReadOnly` — visitor_type decides which inductions a property REQUIRES
 *    (property_induction_requirements), so flipping it midway would silently
 *    change what the visitor still owes. It stays editable in Edit Details,
 *    which is the right place to correct it.
 *  - `arrivalReadOnly` — arrival describes the VISIT, not the induction. An
 *    induction run at 3pm must not rewrite a 9am check-in.
 */
const VisitorDetailsFields = ({
  form,
  visitorType,
  typeReadOnly = false,
  arrivalReadOnly = false,
}) => (
  <>
          <Row gutter={[16, 16]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <Form.Item
                name={["visitor_type"]}
                className="isoInputWrapper"
                label="Type"
                rules={[{ required: true, message: "Select a type" }]}
              >
                <Radio.Group
                  disabled={typeReadOnly}
                  size="large"
                  onChange={() => {
                    // The two pickers share the scheduled_arrival field
                    // but hold different shapes (time-of-day vs full
                    // datetime). Reset to "now" on switch so a stale
                    // contractor date can't leak into the visitor
                    // TimePicker (or vice versa).
                    form.setFieldsValue({ scheduled_arrival: moment() });
                  }}
                >
                  <Radio.Button value={VISITOR_INFO_TYPES.VISITOR}>
                    Visitor
                  </Radio.Button>
                  <Radio.Button value={VISITOR_INFO_TYPES.CONTRACTOR}>
                    Contractor
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              {visitorType === VISITOR_INFO_TYPES.VISITOR ? (
                // Visitors: today only — a bare time picker makes the
                // "current day" rule structural rather than validated.
                <Form.Item
                  name={["scheduled_arrival"]}
                  className="isoInputWrapper"
                  label="Arrival time (today)"
                  rules={[{ required: true, message: "Select an arrival time" }]}
                >
                  <TimePicker
                    disabled={arrivalReadOnly}
                    size="large"
                    style={{ width: "100%" }}
                    format="HH:mm"
                    placeholder="Arrival time today"
                  />
                </Form.Item>
              ) : (
                // Contractors: today or any future date. Time included
                // per spec ("allow date and time; we'll mostly use date").
                <Form.Item
                  name={["scheduled_arrival"]}
                  className="isoInputWrapper"
                  label="Scheduled arrival"
                  rules={[{ required: true, message: "Select a date" }]}
                >
                  <DatePicker
                    disabled={arrivalReadOnly}
                    size="large"
                    style={{ width: "100%" }}
                    showTime={{ format: "HH:mm", defaultValue: moment("08:00", "HH:mm") }}
                    format="DD/MM/YYYY HH:mm"
                    disabledDate={(d) => d && d.isBefore(moment().startOf("day"))}
                    placeholder="Schedule date & time"
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          {/* Contractors carry a business identity on top of the contact's
              personal details — the business name is also the matching key
              that links this check-in to a Contractor-module profile. */}
          {visitorType === VISITOR_INFO_TYPES.CONTRACTOR && (
            <Row gutter={[16, 16]}>
              <Col xs={{ span: 24 }}>
                <Form.Item
                  name={["business_name"]}
                  className="isoInputWrapper"
                  label="Business Name"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Business name is required for contractors",
                    },
                  ]}
                >
                  <Input size="large" placeholder="e.g. TopGun Electrical Services" />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={[16, 16]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <Form.Item
                name={["name"]}
                className="isoInputWrapper"
                label={visitorType === VISITOR_INFO_TYPES.CONTRACTOR ? "Contact Name" : "Name"}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please input your name!",
                  }
                ]}
              >
                <Input size="large" placeholder="Input your name" />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 12}}>
              <Form.Item
                name={["email"]}
                className="isoInputWrapper"
                label="Email"
                rules={[
                  {
                    type: "email",
                    message: "The input is not valid Email!",
                  },
                  {
                    required: true,
                    message: "Please input your Email!",
                  },
                ]}
              >
                <Input size="large" placeholder="Email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
              <Form.Item
                name={["phone"]}
                className="isoInputWrapper"
                label={visitorType === VISITOR_INFO_TYPES.CONTRACTOR ? "Contact Number" : "Mobile Number"}
                rules={[
                  {
                    pattern: /^\d{10,12}$/, // Adjust this pattern as per your phone number format
                    message: "Phone number must be 10 to 12 digits!",
                  },
                  {
                    required: true,
                    message: "Please input your phone number!",
                  }
                ]}
              >
                <Input
                  onKeyDown={(e) => {
                    // Allow only numbers (0-9) and specific control keys
                    if (
                      !/^\d+$/.test(e.key) &&
                      ![
                        "Backspace",
                        "ArrowLeft",
                        "ArrowRight",
                        "Delete",
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  size="large"
                  placeholder="Input your mobile number"
                />
              </Form.Item>
            </Col>
            {/* Birthday stays for regular visitors (summary views and
                existing records rely on it) and is hidden for contractors
                per product — a business check-in doesn't need it. */}
            {visitorType === VISITOR_INFO_TYPES.VISITOR && (
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item
                  name={["birthday"]}
                  className="isoInputWrapper"
                  label="Birthday"
                >
                  <DatePicker
                    size="large"
                    style={{ width: "100%" }}
                    placeholder="Input your birthday"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item
                    name={["emergency_name"]}
                    className="isoInputWrapper"
                    label="Emergency Contact Name"
                >
                <Input size="large" placeholder="Input your emergency name" />
                </Form.Item>
              </Col>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item
                    name={["emergency_mobile_number"]}
                    className="isoInputWrapper"
                    label="Emergency Contact Number"
                    rules={[
                    {
                        pattern: /^\d{10,12}$/, // Adjust this pattern as per your phone number format
                        message: "Phone number must be 10 to 12 digits!",
                    },
                    ]}
                >
                    <Input
                    onKeyDown={(e) => {
                        // Allow only numbers (0-9) and specific control keys
                        if (
                        !/^\d+$/.test(e.key) &&
                        ![
                            "Backspace",
                            "ArrowLeft",
                            "ArrowRight",
                            "Delete",
                        ].includes(e.key)
                        ) {
                        e.preventDefault();
                        }
                    }}
                    size="large"
                    placeholder="Input your emergency mobile number"
                    />
                </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item
                    name={["emergency_contact"]}
                    className="isoInputWrapper"
                    label="Emergency Contact (relationship)"
                >
                <Input size="large" placeholder="Input your emergency contact" />
                </Form.Item>
              </Col>
          </Row>
  </>
);

export default VisitorDetailsFields;
